import React, { useState, useEffect, useContext } from "react";
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Drawer, Switch } from "@mui/material";
import { FeatureFlagService } from "../../services/feature-flag.service";
import { FeatureFlag } from "../../models/feature-flag.model";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import { Button, TextInput } from "../../components";

const emptyFlag: FeatureFlag = {
  flagName: "",
  flagType: "BOOLEAN",
  flagValue: "false",
  description: "",
  changeReason: "",
};

const FeatureFlagsList = () => {
  const toast = useContext(ToastContext);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag>(emptyFlag);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingFlag, setDeletingFlag] = useState<FeatureFlag | null>(null);
  const [auditFlag, setAuditFlag] = useState<FeatureFlag | null>(null);

  useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    setLoading(true);
    try {
      const data = await FeatureFlagService.getAll();
      setFlags(data);
    } catch {
      toast?.openToast("Failed to load feature flags", 2000, ToastVariants.Error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFlag(emptyFlag);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleEdit = (flag: FeatureFlag) => {
    setEditingFlag({ ...flag });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const buildPayload = (flag: FeatureFlag) => ({
    flagName: flag.flagName,
    flagType: flag.flagType || "BOOLEAN",
    flagValue: flag.flagValue,
    description: flag.description || undefined,
    changeReason: flag.changeReason || undefined,
  });

  const handleSave = async () => {
    if (!editingFlag.flagName.trim()) {
      toast?.openToast("Flag name is required", 2000, ToastVariants.Error);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(editingFlag);

      if (isEditing && editingFlag.flagId) {
        const updated = await FeatureFlagService.update(editingFlag.flagId, payload);
        if (updated) {
          toast?.openToast("Feature flag updated");
          setDialogOpen(false);
          fetchFlags();
        } else {
          toast?.openToast("Failed to update feature flag", 2000, ToastVariants.Error);
        }
      } else {
        const created = await FeatureFlagService.create(payload);
        if (created) {
          toast?.openToast("Feature flag created");
          setDialogOpen(false);
          fetchFlags();
        } else {
          toast?.openToast("Failed to create feature flag", 2000, ToastVariants.Error);
        }
      }
    } catch {
      toast?.openToast("An error occurred", 2000, ToastVariants.Error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleValue = async (flag: FeatureFlag) => {
    const newValue = flag.flagValue === "true" ? "false" : "true";
    try {
      const updated = await FeatureFlagService.update(flag.flagId!, {
        flagName: flag.flagName,
        flagType: flag.flagType,
        flagValue: newValue,
        description: flag.description,
        changeReason: `Toggled value to ${newValue}`,
      });
      if (updated) {
        toast?.openToast(`Flag "${flag.flagName}" set to ${newValue}`);
        fetchFlags();
      } else {
        toast?.openToast("Failed to toggle flag", 2000, ToastVariants.Error);
      }
    } catch {
      toast?.openToast("An error occurred", 2000, ToastVariants.Error);
    }
  };

  const handleDeleteConfirm = (flag: FeatureFlag) => {
    setDeletingFlag(flag);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingFlag?.flagId) return;
    try {
      const success = await FeatureFlagService.remove(deletingFlag.flagId);
      if (success) {
        toast?.openToast("Feature flag deleted");
        fetchFlags();
      } else {
        toast?.openToast("Failed to delete feature flag", 2000, ToastVariants.Error);
      }
    } catch {
      toast?.openToast("An error occurred", 2000, ToastVariants.Error);
    } finally {
      setDeleteConfirmOpen(false);
      setDeletingFlag(null);
    }
  };

  const isBoolean = (flag: FeatureFlag) => flag.flagType === "BOOLEAN" || !flag.flagType;

  return (
    <main>
      <div className="container mx-auto p-8">
        <div className="block lg:flex justify-between items-center mb-6">
          <div className="mb-5 lg:mb-0">
            <h2 className="heading mb-0 uppercase whitespace-nowrap">Feature Flags</h2>
          </div>
          <div className="block md:flex lg:justify-end flex-wrap w-full">
            <Button text="Create Feature Flag" onClick={handleCreate} className="whitespace-nowrap w-auto mt-0" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <CircularProgress />
          </div>
        ) : flags.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No feature flags configured</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Flag Name</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Type</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Value</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Description</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {flags.map(flag => (
                  <tr key={flag.flagId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium">{flag.flagName}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{flag.flagType || "BOOLEAN"}</td>
                    <td className="py-3 px-4">
                      {isBoolean(flag) ? (
                        <Switch checked={flag.flagValue === "true"} onChange={() => handleToggleValue(flag)} size="small" />
                      ) : (
                        <span className="text-sm text-gray-600">{flag.flagValue || "—"}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{flag.description || "—"}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(flag)} className="text-sm text-docsigna-purple-dark hover:underline">
                          Edit
                        </button>
                        <button onClick={() => setAuditFlag(flag)} className="text-sm text-docsigna-purple-dark hover:underline">
                          Audit
                        </button>
                        <button onClick={() => handleDeleteConfirm(flag)} className="text-sm text-red-600 hover:underline">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit Feature Flag" : "Create Feature Flag"}</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flag Name *</label>
              <TextInput
                value={editingFlag.flagName}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => setEditingFlag({ ...editingFlag, flagName: e.target.value })}
                placeholder="e.g. mailersend.enabled"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flag Type</label>
              <select
                value={editingFlag.flagType || "BOOLEAN"}
                onChange={e => setEditingFlag({ ...editingFlag, flagType: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="BOOLEAN">BOOLEAN</option>
                <option value="STRING">STRING</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flag Value</label>
              {(editingFlag.flagType || "BOOLEAN") === "BOOLEAN" ? (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingFlag.flagValue === "true"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEditingFlag({ ...editingFlag, flagValue: e.target.checked ? "true" : "false" })
                    }
                  />
                  <span className="text-sm text-gray-600">{editingFlag.flagValue === "true" ? "true" : "false"}</span>
                </div>
              ) : (
                <TextInput
                  value={editingFlag.flagValue || ""}
                  onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => setEditingFlag({ ...editingFlag, flagValue: e.target.value })}
                  placeholder="Value"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <TextInput
                value={editingFlag.description || ""}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => setEditingFlag({ ...editingFlag, description: e.target.value })}
                placeholder="What does this flag control?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Change Reason</label>
              <TextInput
                value={editingFlag.changeReason || ""}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => setEditingFlag({ ...editingFlag, changeReason: e.target.value })}
                placeholder="Why is this being changed?"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button text="Cancel" onClick={() => setDialogOpen(false)} variant="Outlined" width="fit" />
          <Button text={saving ? "Saving..." : "Save"} onClick={handleSave} disabled={saving} width="fit" />
        </DialogActions>
      </Dialog>

      {/* Audit Drawer */}
      <Drawer anchor="right" open={!!auditFlag} onClose={() => setAuditFlag(null)}>
        <div className="w-80 p-6">
          <h3 className="text-lg font-semibold mb-1">Audit Info</h3>
          <p className="text-sm text-gray-500 mb-6">{auditFlag?.flagName}</p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-800 uppercase mb-1">Last Changed By</label>
              <p className="text-sm">{auditFlag?.updatedByEmail || "System"}</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-800 uppercase mb-1">Last Changed Date</label>
              <p className="text-sm">{auditFlag?.updatedDate ? new Date(auditFlag.updatedDate).toLocaleString() : "—"}</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-800 uppercase mb-1">Last Change Reason</label>
              <p className="text-sm">{auditFlag?.changeReason || "—"}</p>
            </div>
          </div>
          <div className="mt-8">
            <Button text="Close" onClick={() => setAuditFlag(null)} variant="Outlined" width="fit" />
          </div>
        </div>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Feature Flag</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{deletingFlag?.flagName}</strong>? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button text="Cancel" onClick={() => setDeleteConfirmOpen(false)} variant="Outlined" width="fit" />
          <Button text="Delete" onClick={handleDelete} variant="orange" width="fit" />
        </DialogActions>
      </Dialog>
    </main>
  );
};

export default FeatureFlagsList;
